import type { ActiveType } from "pages/calculate/store/CalculateVo";
import type { IPageInfo } from "pages/notice/store/NoticeVo";

/**
 * @enum 채널 서비스 상태
 * @property {await} 심사 대기
 * @property {inspect} 심사중(검토중)
 * @property {reject} 반려
 * @property {approval} 승인 완료
 * @property {service} 서비스 중
 * @property {stop} 서비스 중지
 */
export enum ChannelStatusType {
  await = "AWAITING",
  inspect = "INSPECTING",
  reject = "REJECT",
  approval = "APPROVAL",
  service = "SERVICE",
  stop = "STOP",
}

/**
 * @enum RSS 서비스 상태
 * @property {await} 심사 대기
 * @property {inspect} 심사중(검토중)
 * @property {reject} 반려
 * @property {approval} 승인 완료(연동중 or 서비스 시작 전)
 * @property {service} 서비스 중
 * @property {stop} 서비스 중지
 *
 * @property {success} SUCCESS RSS 검증 성공
 * @property {fail} FAIL rss 검증 실패
 */
export enum RSSstatusType {
  await = "AWAITING",
  inspect = "INSPECTING",

  success = "SUCCESS",
  fail = "FAIL",

  approval = "APPROVAL",
  reject = "REJECT",

  service = "SERVICE",
  stop = "STOP",
}

/**
 * 서비스 내부 텝메뉴 타입
 */
export type ServiceActiveTab = ActiveType.CP | ActiveType.CON | ActiveType.PN | "IP";
export type ServiceSubTab = "channel" | "distribute" | "guide" | "manual";

export interface IResServiceChanneList<T> {
  list: Array<T>;
  page_info: IPageInfo;
}

/**
 * @interface IServicChannelInfoVo 서비스 > 콘텐츠 > 채널관리 그리드 데이터
 * @property {number} id id값
 * @property {string} user_name 신청자 이름
 * @property {string} content_provider_code 채널 코드
 * @property {string} content_provider_name 채널명
 * @property {string} created_at 서비스 신청일
 * @property {@link ChannelStatusType} status 서비스 상태
 * @property {number} category_count 연동 카테고리 개수
 * @property {number} expected_unit_price 예상 단가
 */
export interface IServicChannelInfoVo {
  id: number;
  user_name: string;
  content_provider_code: string;
  content_provider_name: string;
  created_at: string;
  status: ChannelStatusType;
  category_count?: number;
  expected_unit_price?: number;
}

/**
 * @interface IServiceMediaChannelInfo 서비스 > 음원/영상 > 채널관리 그리드 데이터
 * @property {string} channel_id 채널 아이디
 * @property {string} channel_name 채널명
 * @property {boolean} delete_flag
 * @property {number} id 아이디
 * @property {string} start_at 서비스 신청일
 * @property {ChannelStatusType} status 서비스 상태
 * @property {string} status_name 상태이름
 * @property {string} user_name 유저명
 */
export interface IServiceMediaChannelInfo {
  channel_id: string;
  channel_name: string;
  delete_flag: boolean;
  id: number;
  start_at: string;
  status: ChannelStatusType;
  status_name: string;
  user_name: string | null;
}

/**
 * @property {boolean} auto_filter_flag 채널 자동배포 여부
 * @property {boolean} trends_flag 채널 트렌스배포 여부
 * @property {IRssListVo[]} list rss 리스트
 */
export interface IResRssInfo {
  auto_filter_flag: boolean;
  trends_flag: boolean;
  list: IRssListVo[];
}
/**
 * @interface IRssListVo rss grid list
 * @property {number} cp_endpoint_id id
 * @property {boolean} cp_endpoint_using_flag rss 서비스 상태
 * @property {string} cp_endpoint_type 타입(viewus, msn)
 * @property {string} link RSS 링크
 * @property {@link RSSstatusType} inspect_status rss 상태
 *
 * @property {number} cp_category_mapping_id RSS와 카테고리의 mapping id
 * @property {string} view_category_name RSS 카테고리
 * @property {string} category_code RSS 카테고리 코드
 * @property {number} category_id 연동 카테고리 id
 * @property {string} category_name 연동 카테고리
 * @property {boolean} cp_category_mapping_using_flag 카테고리 서비스 상태
 * @property {boolean} inspect_apply_flag RSS 검수 여부
 * @property {string} message 상태가 반려 일 경우 반려 사유
 */
export interface IRssListVo {
  cp_endpoint_id?: number;
  endpoint_type?: string;
  cp_endpoint_using_flag?: boolean;
  cp_endpoint_type_list?: string[];
  cp_endpoint_type?: string;
  link?: string;
  inspect_status: RSSstatusType;

  cp_category_mapping_id?: number;
  view_category_name?: string;
  category_code?: string;
  category_id?: number;
  category_name?: string;
  cp_category_mapping_using_flag?: boolean;

  inspect_apply_flag?: boolean;
  message?: string;

  crawler_type?: string;

  // insert시에만 쓸 파라미터
  viewus_link?: string;
  viewus_category_name?: string;
  viewus_category_code?: string;
  viewus_category_id?: number;

  msn_link?: string;
  msn_category_name?: string;
  msn_category_code?: string;
  msn_category_id?: number;
}

/**
 * @interface IRssCategorySelectboxListVo RSS 등록 시 연동카테고리 셀렉트박스 리스트
 * @property {number} category_id 카테고리 id
 * @property {string} category_name 카테고리 명
 */
export interface IRssCategorySelectboxListVo {
  category_id: number;
  category_name: string;
}

/**
 * @interface IRssPopupInfoVo RSS 팝업 관련 데이터들
 * @property {any} categorySelectList rss 관련 카테고리 selectbox list
 * @property {RSSstatusType} admin_status rss의 직접 상태가 아닌 관리자가 클릭 한 상태
 * @property {string} state 관리자 > rss 수정 / 테스트 상태 관리값
 * @property {boolean} re_flag 반려 후 재수정인지 아닌지 체크
 * @property {boolean} loading api 여러번 찌르지 않게하기위해 로딩 관리
 */
export interface IRssPopupInfoVo {
  categorySelectList?: { [key: string]: { id: number; name: string; value: number }[] };
  admin_status?: RSSstatusType;
  // state?: "CHECK" | "UPDATE" | "TEST";
  state?: string;
  re_flag?: boolean;
  loading?: boolean;
}

/**
 * @interface IRssUsingFlagParam RSS 연동 / 해제 using flag시 보낼 파라미터
 * @property {number} category_id 카테고리 id
 * @property {number} cp_category_mapping_id 카테고리 명
 * @property {boolean} cp_category_mapping_using_flag 카테고리 명
 */
export interface IRssUsingFlagParam {
  cp_endpoint_id: number;
  cp_category_mapping_id: number;
  cp_category_mapping_using_flag: boolean;
}
/**
 * @interface IFetchStatusChange 관리자가 서비스 상태 변경할 때 보내주는 Param
 * @property {number} id 변경 할 id
 * @property {RSSstatusType} status 변경 할 상태값
 * @property {string} desc 반려할 경우 반려 사유
 * @property {string} ucid 음원 서비스일 경우 채널 아이디
 */
export interface IFetchStatusChange {
  status: RSSstatusType | ChannelStatusType;
  id: number;
  desc?: string;
  ucid?: string;
}

/**
 * @interface IChannelServiceDetailVo 사용자 서비스 상세
 * @property {number} id id
 * @property {ChannelStatusType} status 상태값
 * @property {string} status_desc 반려 사유
 * @property {string} url 웹사이트 url
 * @property {string} desc 브랜드 소개
 * @property {string} name 웹사이트 이름
 * @property {string} lang 언어
 *
 * @property {ChannelStatusType} admin_status 관리자 일 경우 진짜 상태 값 아닌 예상 상태값을 가지고 화면 분리를 하기 때문에 만듬
 * @property {boolean} re_flag 반려 후 재신청 flag
 * @property {string | null} ucid 음원 채널 등록시 사용할 youtube 채널 아이디
 */
export interface IChannelServiceDetailVo {
  id: number;
  status: ChannelStatusType;
  status_desc?: string;

  url: string;
  desc?: string;
  name: string;
  lang: string;

  admin_status?: ChannelStatusType;
  re_flag?: boolean;
  ucid?: string | null;
}

/**
 * 콘텐츠분석 > 영상/음원 > 음원목록
 * @property {number} uuid
 * @property {string} cover_path 앨범커버
 * @property {string} isrc 앨범 isrc값
 * @property {string} name 트랙명
 * @property {Array<{id: number; name: string}>} tag_list 태그 정보
 * @property {string} sound_path 음원 목록
 * @property {boolean} playFlag 재생여부
 */
export interface IConsaladChannelMusicGridVo {
  id: number;
  cover_path: string;
  isrc: string;
  name: string;
  tag_list: {
    id: number;
    name: string;
  }[];
  sound_path: string;
  playFlag: boolean;
}
